import React, { memo, PropsWithChildren, useMemo } from 'react';
import Div from '../Div';
import Paragraph from '@/shared/Paragraph';
import Heading from '@/shared/Heading';
import { MapType } from '@/types';
import Images from '../Image';
import { parseMediaFromCMS } from '@/utils/image';
import useScreen from '@/hooks/useScreen';
import CTAs from './CTAs';
import dlv from 'dlv';

// Define interface for CTA object
interface CTA {
  label: string;
  url: string;
}

// Enum for Banner component types
export enum BannerComponentType {
  Image = 'Image',
  Video = 'Video',
}

type BannerProps = {
  dataTestId?: string;
  title?: string;
  subTitle?: string;
  description?: string;
  type?: BannerComponentType;
  desktopMedia?: {
    src: string;
    data: {
      attributes: {
        width: number | undefined;
        height: number;
        alternativeText: string;
      };
    };
  };
  mobileMedia?: {
    src: string;
    data: {
      attributes: {
        width: number | undefined;
        height: number;
        alternativeText: string;
      };
    };
  };
  isDesktop?: boolean;
  ctas?: CTA[];
};

const ImageComponent = (props: PropsWithChildren<BannerProps>) => {
  const { desktopMedia, mobileMedia, isDesktop } = props;
  const desktopOrMobileMedia: any = isDesktop ? desktopMedia : mobileMedia;

  const { width, height, alternativeText, url } = dlv(desktopOrMobileMedia, 'data.attributes', {});

  return (
    <Images
      src={url}
      width={width}
      height={height}
      alt={alternativeText}
      className="w-full h-96 object-cover"
    />
  );
};

const VideoComponent = (props: PropsWithChildren<BannerProps>) => {
  const { desktopMedia, mobileMedia, isDesktop } = props;
  return (
    <video className="w-full object-cover h-96" autoPlay loop playsInline muted>
      <source
        src={parseMediaFromCMS(isDesktop ? desktopMedia : mobileMedia)?.src}
        type="video/mp4"
      />
    </video>
  );
};

const BANNER_COMPONENT_MAP: MapType = {
  [BannerComponentType.Image]: ImageComponent,
  [BannerComponentType.Video]: VideoComponent,
};

const VideoBanner = (props: PropsWithChildren<BannerProps>) => {
  const {
    dataTestId,
    title,
    subTitle,
    description,
    type = BannerComponentType.Video,
    ctas,
    mobileMedia,
    desktopMedia,
  } = props;
  const { isDesktop } = useScreen();

  const Comp = BANNER_COMPONENT_MAP[type];

  const mediaProps = useMemo(
    () => ({ isDesktop, mobileMedia, desktopMedia }),
    [isDesktop, mobileMedia, desktopMedia],
  );

  return (
    <Div dataTestId={dataTestId}>
      <Div className="relative">
        <Comp {...mediaProps} />
      </Div>
      <Div className="grid gap-s3 justify-center text-center px-s5">
        <Heading level="1" className="text-h-1 md:text-hd-1 mt-s3 md:mt-6 text-cs-primary">
          {title}
        </Heading>
        <Paragraph className="text-body-xl md:text-body-2xl text-cs-primary">{subTitle}</Paragraph>
        <Paragraph className="md:text-body-xl text-cs-disabled">{description}</Paragraph>
        <CTAs
          list={ctas || []}
          dataTestId="home-banner"
          className="py-s4 px-s6 mt-s5 mb-s6 bg-cs-primary !text-cm-primary !rounded-3xl m-auto hover:bg-cs-primary hover:text-cm-primary"
        />
      </Div>
    </Div>
  );
};

export default memo(VideoBanner);
